import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { loadStripe } from "@stripe/stripe-js";
import { Elements } from "@stripe/react-stripe-js";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faDog, faHome, faHandHoldingHeart } from "@fortawesome/free-solid-svg-icons";
import { generateDonation } from "../api/donate";
import { showcasedPupsFundDogs } from "../api/showcasedPupsFundDogs";
import DonationForm from "../components/DonationForm";
import SuccessPopup from "../components/FosterSuccess/FosterSuccess";
import DogScroller from "../components/DogScroller";
import DogBrowserSection from "../components/DogBrowserSection";
import { PupsFundDogModel } from "../types/pupsFundDogModel";
import heroSection from "../images/donate-landing-hero.png";
import polly from "../images/Polly.jpg";
import elenaS from "../images/elenaS.jpg";
import ethanK from "../images/ethanK.jpg";
import selenaR from "../images/SelenaR.jpg";
import heroMobile from "../images/donate-landing-mobile.png";

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY as string);

const Landing: React.FC = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [amount, setAmount] = useState(0);
  const [customAmount, setCustomAmount] = useState<number | null>(null);
  const [showCustomAmountInput, setShowCustomAmountInput] = useState(false);
  const [clientSecret, setClientSecret] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [showDonationBox, setShowDonationBox] = useState(true);
  const [donationId, setDonationId] = useState<number | null>(null);
  const [showPaymentBox, setShowPaymentBox] = useState(false);
  const [showThankYouMessage, setShowThankYouMessage] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [isRecurring, setIsRecurring] = useState(false);
  const [pupsFundDogs, setPupsFundDogs] = useState<PupsFundDogModel[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [loadError, setLoadError] = useState<string>("");

  useEffect(() => {
    const fetchPupsFundDogs = async () => {
      setIsLoading(true);
      try {
        const response = await showcasedPupsFundDogs();

        // Extract dogs array from the response
        let dogsArray: PupsFundDogModel[] = [];

        if (response && Array.isArray(response) && response.length > 0) {
          if (
            response[0] &&
            typeof response[0] === "object" &&
            response[0].dogs &&
            Array.isArray(response[0].dogs)
          ) {
            dogsArray = response[0].dogs;
          } else {
            dogsArray = response;
          }
        } else if (
          response &&
          typeof response === "object" &&
          response.dogs &&
          Array.isArray(response.dogs)
        ) {
          dogsArray = response.dogs;
        }

        // Simplified processing of dog objects
        const processedDogs = dogsArray.map((dog: any) => ({
          id: Number(dog.id),
          name: String(dog.name).toLowerCase(),
          image: String(dog.image),
          description: String(dog.description || ''),
          amount_needed: Number(dog.amount_needed || 0),
          amount_raised: Number(dog.amount_raised || 0),
          created_at: String(dog.created_at || ''),
        }));

        setPupsFundDogs(processedDogs);
      } catch (error) {
        console.error("Failed to fetch PupsFund dogs:", error);
        setLoadError("Failed to load dogs. Please try again later.");
      } finally {
        setIsLoading(false);
      }
    };

    fetchPupsFundDogs();
  }, []);

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
  };

  const handleAmountClick = (amount: number) => {
    setAmount(amount);
    setShowCustomAmountInput(false);
    handleDonateClick(amount);
  };

  const handleCustomAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/^0+/, ""); // Remove leading zeros
    setCustomAmount(value === "" ? null : Number(value));
  };

  const handleCustomAmountSubmit = () => {
    if (customAmount && !isNaN(customAmount)) {
      setAmount(customAmount);
      handleDonateClick(customAmount);
    } else {
      setErrorMessage("Please enter a valid amount.");
    }
  };

  const handleDonateClick = async (donationAmount: number) => {
    // Make sure email is valid
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email || !emailRegex.test(email)) {
      setErrorMessage("Please enter a valid email.");
      return;
    }
    if (!donationAmount || isNaN(donationAmount)) {
      setErrorMessage("Please enter a valid amount.");
      return;
    }

    setIsProcessing(true);
    try {
      const { donation_id, client_secret } = await generateDonation({
        amount: donationAmount,
        user_email: email || "",
        type: "pups_fund",
        is_recurring: isRecurring,
      });

      setClientSecret(client_secret);
      setDonationId(donation_id);
      setShowDonationBox(false);
      setShowPaymentBox(true);
    } catch (error: any) {
      console.error("Error generating donation:", error);
      const errorMessage =
        typeof error.response?.data === "string"
          ? error.response.data
          : "Error generating donation. Please try again.";
      setErrorMessage(errorMessage);
      setIsProcessing(false);
    }
  };

  return (
    <main className="font-poppins text-gray-dark">

      {/* Hero Section */}
      {/* Desktop Version */}
      <section
        className="hidden md:flex bg-no-repeat bg-cover bg-center h-[600px] items-center"
        style={{
          backgroundImage: `url(${heroSection})`,
        }}
      >
        <div className="max-w-7xl mx-auto px-12 w-full">
          <div className="max-w-xl">
            <h1 className="text-white text-5xl lg:text-6xl font-bold font-poppins drop-shadow-lg mb-6 leading-tight">
              GIVE A DOG
              A SECOND CHANCE
            </h1>
            <p className="text-black text-base lg:text-lg mb-8 leading-relaxed max-w-lg">
              Your support helps dogs in urgent need get medical care,
              safety &amp; a path to a forever home — every day of the year.
            </p>
            <button
              onClick={() => {
                const donationSection = document.getElementById('donation-form');
                donationSection?.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }}
              className="bg-yellow hover:bg-yellow/90 text-white px-10 py-3 font-semibold rounded-full shadow transition text-lg"
            >
              Donate Now
            </button>
          </div>
        </div>
      </section>

      {/* Mobile Version */}
      <section className="md:hidden">
        <div className="bg-blue px-6 py-8 text-center">
          <h1 className="text-white text-3xl font-bold font-poppins mb-4 leading-tight">
            GIVE A DOG A
            <br />
            SECOND CHANGE
          </h1>
          <p className="text-black text-sm mb-6 leading-relaxed max-w-xs mx-auto">
            Your support helps dogs in urgent need get medical care,
            safety &amp; a path to a forever home — every day of the year.
          </p>
          <button
            onClick={() => {
              const donationSection = document.getElementById('donation-form');
              donationSection?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            }}
            className="bg-yellow hover:bg-yellow/90 text-white px-8 py-3 font-semibold rounded-full shadow transition"
          >
            Donate Now
          </button>
        </div>
        <img
          src={heroMobile}
          alt="Hero"
          className="w-full h-auto object-cover"
        />
      </section>

      {/* Donation Form */}
      <section id="donation-form" className="bg-white py-16 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">

            {/* Left Column - Donation Form or Payment Box */}
            <div className="flex flex-col items-center">
              {showDonationBox && (
                <>
                  <div className="mb-4 flex flex-col items-center w-full">
                    <label className="mb-2 text-xl font-semibold text-blue">
                      Email
                    </label>
                    <input
                      className="mb-4 border-2 px-2 py-1 w-full max-w-md"
                      type="email"
                      value={email}
                      onChange={handleEmailChange}
                      placeholder="Enter Email"
                    />
                  </div>

                  {/* Donation Frequency Toggle */}
                  <div className="mb-6 flex items-center">
                    <div className="relative inline-flex">
                      <div className="flex rounded-full bg-gray-200 p-1">
                        <button
                          className={`rounded-full px-4 py-2 transition-colors ${
                            !isRecurring ? "bg-blue text-white" : "text-gray-700"
                          }`}
                          onClick={() => setIsRecurring(false)}
                        >
                          One-time
                        </button>
                        <button
                          className={`rounded-full px-4 py-2 transition-colors ${
                            isRecurring ? "bg-blue text-white" : "text-gray-700"
                          }`}
                          onClick={() => setIsRecurring(true)}
                        >
                          Monthly
                        </button>
                      </div>
                    </div>
                  </div>

                  <p className="mb-4 text-xl font-semibold text-blue">
                    Select an Amount
                  </p>
                  <div className="mb-4 flex w-full max-w-md flex-wrap justify-center">
                    {[10, 20, 50, 100].map((amount) => (
                      <button
                        key={amount}
                        className="mx-2 mt-2 rounded bg-blue px-4 py-2 font-bold text-white hover:bg-blue-dark"
                        onClick={() => handleAmountClick(amount)}
                        disabled={isProcessing}
                      >
                        ${amount}
                      </button>
                    ))}
                    <button
                      className="mx-2 mt-2 rounded bg-blue px-4 py-2 font-bold text-white hover:bg-blue-dark"
                      onClick={() => {
                        setCustomAmount(null);
                        setShowCustomAmountInput(true);
                      }}
                      disabled={isProcessing}
                    >
                      Other
                    </button>
                  </div>
                  {showCustomAmountInput && (
                    <div className="flex flex-col items-center gap-2 sm:flex-row sm:gap-0">
                      <label className="mr-2">Enter Donation amount:</label>
                      <input
                        className="border-2 px-2 py-1"
                        type="number"
                        value={customAmount ?? ""}
                        onChange={handleCustomAmountChange}
                        min="1"
                      />
                      <button
                        className="ml-2 rounded bg-blue px-4 py-2 font-semibold text-white hover:bg-blue-dark"
                        onClick={handleCustomAmountSubmit}
                        disabled={isProcessing}
                      >
                        Proceed
                      </button>
                    </div>
                  )}
                  {errorMessage && <p className="mt-2 text-red-500">{errorMessage}</p>}
                </>
              )}

              {showPaymentBox && clientSecret && (
                <div className="w-full">
                  <div className="flex flex-col items-center">
                    <h2 className="text-center text-2xl font-bold text-blue mb-4">
                      Complete your {isRecurring ? "monthly" : "one-time"} donation of $
                      {amount}.00
                    </h2>
                    <p className="text-lg mb-6">Thank You For Your Support!</p>
                  </div>
                  <Elements stripe={stripePromise} options={{ clientSecret }}>
                    <DonationForm
                      clientSecret={clientSecret}
                      donationId={donationId}
                      setShowPaymentBox={setShowPaymentBox}
                      setShowThankYouMessage={setShowThankYouMessage}
                      setPaymentStatus={() => { }}
                    />
                  </Elements>
                </div>
              )}
            </div>

            {/* Right Column - Content */}
            <div className="flex flex-col justify-center">
              <h2 className="text-3xl md:text-4xl font-bold text-black mb-6">
                Change a Life Today
              </h2>

              <div className="space-y-4 text-gray-700 leading-relaxed">
                <p>
                  Every dog we rescue has a story — and a future waiting to happen.
                  Your donation helps provide medical care, transport, foster support and
                  everything needed to say <strong>"yes"</strong> when a dog needs help most.
                </p>
                <p>
                  With your support, we can keep rescuing dogs year-round and give them
                  the chance they deserve.
                </p>
              </div>

              <h3 className="text-xl font-bold text-black mt-8 mb-3">
                Make an Immediate Impact
              </h3>
              <p className="text-gray-700 leading-relaxed">
                Your tax-deductible gift goes directly toward lifesaving care and rescue
                efforts. Your generosity makes everything from emergency medical
                treatment to helping a dog find their forever home possible.
              </p>
            </div>

          </div>
        </div>
      </section>

      
      {showThankYouMessage && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <SuccessPopup
            message="Thank you for your donation to Pup Profile! A confirmation will be sent to the email provided."
            onClose={() => {
              setShowThankYouMessage(false);
              window.location.href = "/";
            }}
          />
        </div>
      )}

      {/* Dog Browser Section */}
      <DogBrowserSection />

      {/* Pups We've Helped */}
      <section className="my-8 px-4 md:px-8">
        <h2 className="mb-8 text-center text-2xl font-bold text-black">
          Pups We've Helped
        </h2>

        {isLoading && (
          <div className="flex justify-center">
            <div className="h-12 w-12 animate-spin rounded-full border-4 border-blue border-t-transparent"></div>
          </div>
        )}

        {loadError && (
          <div className="mx-auto max-w-2xl text-center text-red-500">
            {loadError}
          </div>
        )}

        {!isLoading && !loadError && pupsFundDogs.length === 0 && (
          <p className="text-center text-gray-dark">
            We are currently working on getting more dogs funded. Please check
            back soon!
          </p>
        )}

        {!isLoading && !loadError && pupsFundDogs.length > 0 && (
          <div className="relative mx-auto mt-6 max-w-full overflow-hidden md:max-w-[1300px]">
            <DogScroller dogs={pupsFundDogs} />
          </div>
        )}
      </section>


      {/* Join the Mission */}
      <section className="bg-blue py-16 px-6">
        <div className="max-w-[1600px] mx-auto">
          <h2 className="text-3xl font-bold text-black text-center mb-12">Join the Mission</h2>
          <div className="grid md:grid-cols-3 gap-6 max-w-6xl mx-auto">
            {[
              {
                title: "Foster",
                desc: "You don't need forever to change a life—just a little space and love to foster.",
                icon: faDog,
                link: "/get-involved",
              },
              {
                title: "Adopt",
                desc: "When you adopt, you're not just saving a life — you're giving love, hope, and a forever home.",
                icon: faHome,
                link: "/get-involved",
              },
              {
                title: "Donate",
                desc: "Your generosity gives food, shelter, and hope to animals who need it most.",
                icon: faHandHoldingHeart,
                link: "/donate",
              },
            ].map((block) => (
              <div
                key={block.title}
                className="transform scale-90 hover:scale-95 transition-transform duration-300"
              >
                <div className="bg-white rounded-lg p-8 shadow text-center h-full flex flex-col items-center">
                  <FontAwesomeIcon 
                    icon={block.icon} 
                    className="text-yellow text-8xl mb-6"
                  />
                  <h3 className="text-xl font-bold text-black mb-4">{block.title}</h3>
                  <p className="text-sm text-gray-700 mb-6 flex-grow">{block.desc}</p>
                  <button 
                    onClick={() => {
                      navigate(block.link);
                      window.scrollTo(0, 0);
                    }}
                    className="bg-blue text-white px-6 py-2 rounded-md font-semibold hover:bg-blue-dark transition"
                  >
                    Learn More
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 👑 Pup Heroes */}
      {/* <section className="bg-white py-16 px-6 text-center">
        <h2 className="text-2xl font-bold text-blue mb-8">Pup Heroes</h2>
        <div className="flex flex-wrap justify-center gap-16 items-center mb-10">
          {[ellipse2, ellipse3, ellipse4].map((logo, index) => (
            <img
              key={index}
              src={logo}
              alt={`hero-${index + 1}`}
              className="h-40 md:h-52 object-contain"

            />
          ))}
        </div>
      </section> */}

      {/* ✨ CTA Right Block */}
      <section className="bg-white py-16 px-6">
        <div className="flex flex-col md:flex-row max-w-6xl mx-auto items-center gap-20">
          <div className="flex-1">
            <img
              src={polly}
              alt="Dog in wheelchair"
              className="rounded-2xl border-4 border-blue"
            />
          </div>
          <div className="flex-1 flex flex-col gap-6">
            <h2 className="text-3xl font-bold text-black">
              Give Every Dog a Chance at a Better Life
            </h2>
            <p className="text-gray-700">
              Pup Profile connects vulnerable dogs with people like you — heroes who foster, adopt, and support their journeys to forever homes. Whether you give once or monthly, your kindness writes a new chapter in a pup's story.
            </p>
            <button
              onClick={() => {
                const donationSection = document.getElementById('donation-form');
                donationSection?.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }}
              className="bg-yellow hover:bg-yellow/90 text-black w-fit mx-auto px-8 py-3 rounded-lg font-semibold"
            >
              Donate Now
            </button>
          </div>
        </div>
      </section>

      {/* 🌟 Testimonials */}
      <section className="bg-blue py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl font-bold text-black mb-12 text-left">Loved by Our Supporters</h2>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                name: "Ethan Knox",
                role: "Pet Lover",
                quote:
                  "Thanks to this amazing team, I found my perfect furry companion. Their care and honesty made the whole process stress-free!",
                image: ethanK,
              },
              {
                name: "Elena Storm",
                role: "Pet Lover",
                quote:
                  "Fostering with them has been so fulfilling and meaningful. The support they give foster families is truly incredible—I never felt alone in the process.",
                image: elenaS,
              },
              {
                name: "Selena Rae",
                role: "Pet Lover",
                quote:
                  "You can tell this team truly loves animals. Their dedication to finding safe, loving homes is inspiring. I'm proud to be part of this journey.",
                image: selenaR,
              },
            ].map((t) => (
              <div
                key={t.name}
                className="bg-gray-50 rounded-2xl shadow-lg p-8 text-left flex flex-col gap-4 min-h-[280px]"
              >
                {/* Profile row */}
                <div className="flex items-center gap-4">
                  <img
                    src={t.image}
                    alt={t.name}
                    className="h-12 w-12 rounded-full object-cover"
                  />
                  <div>
                    <p className="font-bold">{t.name}</p>
                    <p className="text-sm text-gray-500">{t.role}</p>
                  </div>
                </div>

                {/* Quote */}
                <p className="text-gray-700">{t.quote}</p>

                {/* Rating */}
                <div className="text-right text-blue text-xl">★★★★★</div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
};

export default Landing;
